import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import Configuracao from '../util/Configuracao';
import NomeUtil from '../util/NomeUtil';
import Store from './Store';
import moment from 'moment';
import { RESULTADO_JULGAMENTO } from '@/dominio/ResultadoJulgamento';
import { LOCALSTORAGE_STORAGE } from '@/util/StorageUtil';

/**
 * Classe utilizada para guardar e manipular o estado da aplicação.
 * Deve-se criar uma para cada contexto ou entidade.
 */
export default class DominioStore extends Store {
    procuradores = [];
    classesProcesso = [];
    // Lista de movimentos CNJ para processos do PJe
    movimentosProcessuais = [];
    // Listas de domínios de complemento para cada movimento CNJ
    dominiosComplementosMovimentos = {};
    // Lista de decisões para processos do SIJ
    decisoesProcessuais = [];
    magistrados = [];
    tiposResultadoJulgamento = [];
    tiposPendenciasFechamento = [];
    descricoesCargos = [];
    signatariosCertidao = [];
    tiposJulgamento = [];
    tiposInclusaoPauta = [
        { tipoInclusao: 'APTO_PAUTA', descricao: 'Apto para pauta' },
        { tipoInclusao: 'APTO_JULGAMENTO', descricao: 'Apto para sessão' },
        { tipoInclusao: 'REMANESCENTE', descricao: 'Remanescente' },
        { tipoInclusao: 'AGRAVO_PROVIDO', descricao: 'Agravo provido' }
    ];
    feriados = [];
    dataFeriadosPublicacao = [];
    dataFeriadosVotacao = [];

    constructor(props) {
        super(props);

        makeObservable(this, {
            procuradores: observable,
            classesProcesso: observable,
            movimentosProcessuais: observable,
            dominiosComplementosMovimentos: observable,
            decisoesProcessuais: observable,
            magistrados: observable,
            tiposResultadoJulgamento: observable,
            tiposPendenciasFechamento: observable,
            descricoesCargos: observable,
            signatariosCertidao: observable,
            tiposJulgamento: observable,
            tiposInclusaoPauta: observable,
            feriados: observable,
            dataFeriadosPublicacao: observable,
            dataFeriadosVotacao: observable,
            procuradoresSIJ: computed,
            procuradoresPJe: computed,
            classesProcessoSIJ: computed,
            classesProcessoPJe: computed,
            magistradosComSigla: computed,
            movimentosProcessuaisPadronizados: computed,
            identificarComplementosExcludentes: action,
            ordenarComplementos: action,
            complementarInformacoesMovimento: action,
            ajustarComplementos: action,
            substituirDescricoesGruposComplemento: action,
            substituirDescricoesComplementos: action,
            opcoesClasseProcessualPJe: computed
        });
    }

    get storageType() {
        return LOCALSTORAGE_STORAGE;
    }

    async inicializar() {
        super.inicializar();

        await Promise.all([
            this.carregarMovimentos(),
            this.carregarDecisoes(),
            this.carregarClasses(),
            this.carregarProcuradores(),
            this.carregarMagistrados(),
            this.carregarTiposResultadoJulgamento(),
            this.carregarTiposPendenciasFechamento(),
            this.carregarDescricoesCargos(),
            this.carregarSignatariosCertidao(),
            this.carregarTiposJulgamento(),
            this.carregarFeriados()
        ]);
    }

    carregarTiposPendenciasFechamento = async () => {
        let tiposPendenciasFechamento = await this.sessaoService.recuperarTiposPendenciasFechamento();
        runInAction(() => (this.tiposPendenciasFechamento = tiposPendenciasFechamento));
    };

    async carregarProcuradores() {
        if (this.rootStore.parametroStore.isComponenteHabilitado('ProcuradorSessao')) {
            let procuradores = await this.secretariaService.recuperarProcuradores();
            runInAction(() => (this.procuradores = procuradores));
        }
    }

    get procuradoresSIJ() {
        if (this.rootStore.parametroStore.isOrgaoTST) {
            return this.procuradores.filter((procurador) => procurador.origem === Configuracao.ORIGEM_SIJ);
        }
        return [];
    }

    get procuradoresPJe() {
        return this.procuradores.filter((procurador) => procurador.origem === Configuracao.ORIGEM_PJE);
    }

    async carregarClasses() {
        let classes = await this.secretariaService.recuperarClasses();
        runInAction(() => (this.classesProcesso = classes));
    }

    get classesProcessoSIJ() {
        return this.classesProcesso.filter((classe) => classe.origem === Configuracao.ORIGEM_SIJ);
    }

    get classesProcessoPJe() {
        let classesPJe = this.classesProcesso.filter((classe) => classe.origem === Configuracao.ORIGEM_PJE);
        classesPJe = classesPJe.filter((classe) => classe.sigla !== undefined && classe.sigla.trim().length > 0);
        let classesPJeUnicas = [];
        classesPJe.forEach((classe) => {
            if (classesPJeUnicas.findIndex((classeUnica) => classeUnica.sigla === classe.sigla) === -1) {
                classesPJeUnicas.push(classe);
            }
        });
        return classesPJeUnicas;
    }

    async carregarMagistrados() {
        const magistrados = await this.secretariaService.recuperarMagistrados();

        magistrados.forEach((magistrado) => {
            magistrado.nome = NomeUtil.ajustarCaixaNomeProprio(magistrado.nome);
            magistrado.converterSigla = false;

            if (
                magistrado.sigla &&
                magistrado.siglaAdministrativa &&
                magistrado.sigla !== magistrado.siglaAdministrativa
            ) {
                magistrado.converterSigla = true;
            }
        });

        runInAction(() => {
            this.magistrados = magistrados;
        });

        const magistradosSemSigla = magistrados.filter((magistrado) => !magistrado.sigla);

        if (magistradosSemSigla.length > 0) {
            console.error('Estes magistrados se encontram sem sigla:', magistradosSemSigla);
        }
    }

    /**
     * @param {string} sigla
     */
    getMagistradoPelaSigla(sigla) {
        return this.magistrados.find((/** @type {Object} */ mag) => mag.sigla === sigla);
    }

    /**
     * @param {string} cpf
     */
    getMagistradoPeloCpf(cpf) {
        return this.magistrados.find((/** @type {Object} */ mag) => mag.cpf === cpf);
    }

    getEntradaDominioMagistrado(magistrado) {
        if (!magistrado?.cpf) return null;
        const mesmoCpf = this.magistrados.filter((m) => m.cpf && m.cpf === magistrado.cpf);
        if (mesmoCpf.length === 0) return null;

        if (magistrado.siglaAdministrativa) {
            const exato = mesmoCpf.find(
                (m) => m.sigla === magistrado.sigla && m.siglaAdministrativa === magistrado.siglaAdministrativa
            );
            if (exato) return exato;
        }

        const porSigla = mesmoCpf.filter((m) => m.sigla === magistrado.sigla);
        if (porSigla.length > 0) {
            const pura = porSigla.find((m) => m.sigla === m.siglaAdministrativa);
            return pura || porSigla[0];
        }

        return mesmoCpf.find((m) => m.siglaAdministrativa === magistrado.sigla) || null;
    }

    getVariantesSiglaMagistrado(entradaBase) {
        if (!entradaBase) return [];
        return this.magistrados.filter((m) => m.cpf === entradaBase.cpf && m.sigla === entradaBase.sigla);
    }

    async carregarTiposResultadoJulgamento() {
        let retorno = await this.processoService.recuperarTiposResultadosJulgamento();

        runInAction(() => {
            this.tiposResultadoJulgamento = retorno.map((tipoResultado) => {
                if (tipoResultado.codigo === RESULTADO_JULGAMENTO.NAO_JULGADO.codigo) {
                    tipoResultado.descricao = 'Não apreciado';
                }

                return tipoResultado;
            });
        });
    }

    async carregarDescricoesCargos() {
        let retorno = await this.secretariaService.recuperarDescricoesCargos();
        runInAction(() => (this.descricoesCargos = retorno));
    }

    async carregarSignatariosCertidao() {
        let retorno = await this.secretariaService.recuperarSignatariosCertidao();
        runInAction(() => (this.signatariosCertidao = retorno));
    }

    async carregarTiposJulgamento() {
        let retorno = await this.tiposJulgamentoService.recuperarTiposJulgamento();
        runInAction(() => (this.tiposJulgamento = retorno));
    }

    getDescricaoCargo(siglaCargo, genero, plural) {
        plural = plural ? plural : false;

        let descricaoCargo = this.descricoesCargos.find(
            (cargo) => cargo.plural === plural && cargo.genero === genero && cargo.siglaCargo === siglaCargo
        );

        if (!descricaoCargo) {
            // Se não houver a sigla do cargo cadastrada, usa o cargo de Ministro, o qual está cadastrado sem sigla.
            descricaoCargo = this.descricoesCargos.find(
                (cargo) => cargo.plural === plural && cargo.genero === genero && !cargo.siglaCargo
            );
        }

        return descricaoCargo;
    }

    async carregarMovimentos() {
        let movimentosProcessuais = await this.processoService.recuperarMovimentosProcessuais();
        runInAction(() => (this.movimentosProcessuais = movimentosProcessuais));
    }

    async carregarDecisoes() {
        if (this.rootStore.parametroStore.isOrgaoTST) {
            let decisoesProcessuais = await this.processoService.recuperarDecisoesProcessuais();
            runInAction(() => (this.decisoesProcessuais = decisoesProcessuais));
        }
    }

    descricaoClasseProcesso(origem, siglaClasse) {
        let classe = this.getClassesProcesso(origem).find((classeProcesso) => classeProcesso.sigla === siglaClasse);
        return classe ? classe.descricao : '';
    }

    getClassesProcesso(origem) {
        return origem === Configuracao.ORIGEM_SIJ ? this.classesProcessoSIJ : this.classesProcessoPJe;
    }

    get magistradosComSigla() {
        let retorno = this.magistrados ? this.magistrados.filter((magistrado) => magistrado.sigla) : [];
        return retorno;
    }

    opcoesMagistradosComposicao(registro) {
        if (registro) {
            let opcoesMagistrados = registro.composicao.map((comp) => ({
                valor: comp.magistrado,
                texto: comp.magistrado.sigla,
                textoSecundario: NomeUtil.ajustarCaixaNomeProprio(comp.magistrado.nome)
            }));
            return this.sortedMagistrados(opcoesMagistrados, registro);
        }
        return [];
    }

    /**
     * Caso o registro não seja passado como parâmetro, os magistrados da composição não serão ordenados no começo da listagem.
     *
     * @param {*} registro
     * @returns
     */
    opcoesMagistradosAutoComplete(registro) {
        let opcoesMagistrados = this.magistradosComSigla.map((magistrado) => ({
            valor: magistrado,
            texto: magistrado.converterSigla
                ? magistrado.sigla + ' (como ' + magistrado.siglaAdministrativa + ')'
                : magistrado.sigla,
            textoSecundario: NomeUtil.ajustarCaixaNomeProprio(magistrado.nome)
        }));
        return this.sortedMagistrados(opcoesMagistrados, registro);
    }

    sortedMagistrados(listaMagistrados, registro) {
        if (registro && registro.composicao) {
            let sorted = listaMagistrados.slice().sort((mag1, mag2) => {
                let mag1IsComposicao = registro.composicao.find((magistradoComposicao) => {
                    if (!magistradoComposicao.magistrado.sigla) {
                        return false;
                    }
                    return (
                        magistradoComposicao.magistrado.sigla === mag1.valor.sigla ||
                        (magistradoComposicao.magistrado.siglaSubstituto &&
                            magistradoComposicao.magistrado.siglaSubstituto === mag1.valor.sigla)
                    );
                });

                let mag2IsComposicao = registro.composicao.find((magistradoComposicao) => {
                    if (!magistradoComposicao.magistrado.sigla) {
                        return false;
                    }
                    return (
                        magistradoComposicao.magistrado.sigla === mag2.valor.sigla ||
                        (magistradoComposicao.magistrado.siglaSubstituto &&
                            magistradoComposicao.magistrado.siglaSubstituto === mag2.valor.sigla)
                    );
                });

                if (mag1IsComposicao) {
                    return -1;
                } else if (mag2IsComposicao) {
                    return 1;
                } else {
                    return 0;
                }
            });
            sorted = sorted.sort((comp1, comp2) => {
                if (registro.processo.relator && comp1.valor.sigla === registro.processo.relator.sigla) {
                    return -1;
                } else if (registro.processo.relator && comp2.valor.sigla === registro.processo.relator.sigla) {
                    return 1;
                } else if (registro.processo.redator && comp1.valor.sigla === registro.processo.redator.sigla) {
                    return -1;
                } else if (registro.processo.redator && comp2.valor.sigla === registro.processo.redator.sigla) {
                    return 1;
                } else {
                    return 0;
                }
            });
            return observable(sorted);
        }
        return listaMagistrados;
    }

    /**
     * Converte o objeto de movimento processual do formato do PJe para o formato utilizado nos backends do secretaria.
     */
    get movimentosProcessuaisPadronizados() {
        let retorno = [];

        if (this.movimentosProcessuais) {
            retorno = this.movimentosProcessuais.map((movimento) => {
                let complementos = this.converterComplementos(movimento);
                this.identificarComplementosExcludentes(movimento, complementos);
                complementos = this.ordenarComplementos(movimento, complementos);
                return observable({
                    numeroMovimentoCnj: parseInt(movimento.codEvento, 10),
                    descricao: movimento.descricao,
                    complementos: complementos,
                    dominiosComplementos: [],
                    gruposDeComplemento: movimento.gruposDeComplemento,
                    ativo: movimento.ativo
                });
            });
        }
        return retorno;
    }

    converterComplementos(movimento) {
        return movimento.aplicacaoMovimentoList &&
            movimento.aplicacaoMovimentoList.length > 0 &&
            movimento.aplicacaoMovimentoList[0] &&
            movimento.aplicacaoMovimentoList[0].aplicacaoComplementoList
            ? movimento.aplicacaoMovimentoList[0].aplicacaoComplementoList.map((complemento) =>
                  observable({
                      codigoComplementoCnj: parseInt(complemento.tipoComplemento.codigoTipoComplemento, 10),
                      label: complemento.tipoComplemento.label,
                      nome: complemento.tipoComplemento.nome,
                      tipo: complemento.tipoComplemento.tipo
                  })
              )
            : [];
    }

    identificarComplementosExcludentes(movimento, complementos) {
        if (complementos && complementos.length > 1 && movimento.descricao.indexOf('/') > -1) {
            let gruposDeComplemento = movimento.descricao.match(
                /#\{[a-zA-Z0-9\u00C0-\u00FF_\s]*}(?:\s*\/\s*#\{[a-zA-Z0-9\u00C0-\u00FF_\s]*}){1,}/g
            );

            if (gruposDeComplemento) {
                movimento.gruposDeComplemento = [];
                gruposDeComplemento.forEach((grupo) => {
                    let tagsComplemento = grupo.match(/#\{[a-zA-Z0-9\u00C0-\u00FF_\s]*}/g);

                    let complementosComExcludente = tagsComplemento.map((tagComplemento) => {
                        return complementos.find((complemento) => {
                            return (
                                '#{' + this.corrigirString(complemento.nome) + '}' ===
                                this.corrigirString(tagComplemento)
                            );
                        });
                    });

                    movimento.gruposDeComplemento.push({ tag: grupo, complementos: complementosComExcludente });

                    complementosComExcludente.forEach((complementoComExcludente) => {
                        const complementosExcludentes = [];
                        complementosComExcludente.forEach((complemento) => {
                            if (complemento.codigoComplementoCnj !== complementoComExcludente.codigoComplementoCnj) {
                                complementosExcludentes.push(JSON.parse(JSON.stringify(complemento)));
                            }
                        });
                        complementoComExcludente.complementosExcludentes = complementosExcludentes;
                    });
                });
            }
        }
    }

    ordenarComplementos(movimento, complementos) {
        if (complementos && complementos.length > 1) {
            let tagsComplemento = movimento.descricao.match(/#\{[\w\s]*}/g);
            let ordem = 1;
            tagsComplemento &&
                tagsComplemento.forEach((tagComplemento) => {
                    let complemento = complementos.find(
                        (complemento) =>
                            '#{' + this.corrigirString(complemento.nome) + '}' === this.corrigirString(tagComplemento)
                    );

                    if (complemento) {
                        complemento.numeroOrdem = ordem++;
                    }
                });
        }
        return complementos ? complementos.slice().sort((c1, c2) => c1.numeroOrdem - c2.numeroOrdem) : complementos;
    }

    /**
     * Devido à falta de padronização nos nomes dos complementos, é necessário subsituir os underlines por espaços.
     *
     * @param {*} string
     * @returns
     */
    corrigirString(string) {
        return string ? string.replace(/_/g, ' ').trim() : '';
    }

    /**
     * Faz cache e inicialização tardia dos valores de domínio para os complementos de um movimento.
     *
     * @param {*} numeroMovimentoCnj
     * @returns
     */
    async recuperarDominiosComplementos(numeroMovimentoCnj) {
        let dominiosComplementos = this.dominiosComplementosMovimentos[numeroMovimentoCnj];

        if (!dominiosComplementos) {
            dominiosComplementos = await this.movimentoCNJService.recuperarValoresComplementos(numeroMovimentoCnj);
            runInAction(() => (this.dominiosComplementosMovimentos[numeroMovimentoCnj] = dominiosComplementos));
        }
        return dominiosComplementos;
    }

    signatariosCadastradosOjc(siglaOjc) {
        return this.signatariosCertidao.filter((signatario) => signatario.codOrgaoJudicante === siglaOjc);
    }

    signatarioCadastradoDefaultOjc(siglaOjc) {
        return (
            this.signatariosCadastradosOjc(siglaOjc).find((signatario) => signatario.substituto === false) ||
            this.signatariosCadastradosOjc(siglaOjc)[0]
        );
    }

    temSignatariosCadastradosOjc(siglaOjc) {
        return this.signatariosCadastradosOjc(siglaOjc).length > 0;
    }

    async complementarInformacoesMovimento(movimentoProcesso) {
        if (movimentoProcesso && movimentoProcesso.numeroMovimentoCnj) {
            let movimentoOriginal = this.movimentoOriginal(movimentoProcesso.numeroMovimentoCnj);

            if (movimentoOriginal) {
                movimentoProcesso.descricao = movimentoOriginal.descricao;
                movimentoProcesso.gruposDeComplemento = movimentoOriginal.gruposDeComplemento;
                movimentoProcesso.dominiosComplementos = await this.recuperarDominiosComplementos(
                    movimentoOriginal.numeroMovimentoCnj
                );
                this.ajustarComplementos(movimentoOriginal, movimentoProcesso);
            } else {
                console.error(
                    'Movimento cadastrado para o processo não encontrado na lista de Movimentos CNJ: ' +
                        movimentoProcesso.numeroMovimentoCnj
                );
            }
        }
    }

    ajustarComplementos(movimentoOriginal, movimentoProcesso) {
        if (movimentoOriginal.complementos && movimentoOriginal.complementos.length > 0) {
            let complementos = [];
            movimentoOriginal.complementos.forEach((complementoOriginal) => {
                let copiaComplemento = complementoOriginal;
                let complementoProcesso = (movimentoProcesso.complementos || []).find(
                    (compl) => compl.codigoComplementoCnj === complementoOriginal.codigoComplementoCnj
                );

                if (complementoProcesso) {
                    copiaComplemento.valor = complementoProcesso.valor;
                    copiaComplemento.codigoDominioComplementoCnj = complementoProcesso.codigoDominioComplementoCnj;
                }
                complementos.push(copiaComplemento);
            });
            movimentoProcesso.complementos = complementos;
        }
    }

    // Movimento originário da lista de movimentos do cnj, a qual possui todas as opções de complemento para cada movimento
    movimentoOriginal = (numeroMovimento) => {
        return this.movimentosProcessuaisPadronizados.find(
            (movimento) => movimento.numeroMovimentoCnj === numeroMovimento
        );
    };

    substituirDescricoesGruposComplemento(descricaoMovimento, movimento) {
        if (movimento.gruposDeComplemento && movimento.gruposDeComplemento.length > 0) {
            movimento.gruposDeComplemento.forEach((grupo) => {
                let complementoPreenchido;
                grupo.complementos.forEach((complementoGrupo) => {
                    let complementoComValor = movimento.complementos.find(
                        (complementoMovimento) =>
                            complementoMovimento.codigoComplementoCnj === complementoGrupo.codigoComplementoCnj &&
                            this.isComplementoPreenchido(complementoMovimento)
                    );
                    complementoPreenchido = complementoPreenchido || complementoComValor;
                });

                if (complementoPreenchido) {
                    descricaoMovimento = descricaoMovimento.replace(
                        grupo.tag,
                        this.descricaoComplemento(movimento, complementoPreenchido)
                    );
                } else {
                    descricaoMovimento = this.substituirDescricoesComplementos(
                        descricaoMovimento,
                        movimento,
                        grupo.complementos
                    );
                }
            });
        }
        return descricaoMovimento;
    }

    substituirDescricoesComplementos(descricaoMovimento, movimento, complementos) {
        complementos.forEach((complemento) => {
            let descricaoComplemento = this.descricaoComplemento(movimento, complemento);
            descricaoMovimento = descricaoMovimento.replace(
                '#{' + this.corrigirString(complemento.nome) + '}',
                descricaoComplemento
                    ? descricaoComplemento
                    : '<span style="color: orange">"' +
                          this.corrigirString(complemento.nome).toUpperCase() +
                          ' NÃO INFORMADO"</span>'
            );
        });
        return descricaoMovimento;
    }

    descricaoComplemento = (movimento, complemento) => {
        if (complemento.tipo === Configuracao.TIPO_COMPLEMENTO_DOMINIO) {
            let dominioComplemento = this.recuperarDominioComplemento(movimento, complemento);
            return dominioComplemento ? dominioComplemento.valor : '';
        } else if (complemento.codigoComplementoCnj === Configuracao.COD_COMPLEMENTO_CLASSE) {
            let opcaoClasse = this.opcoesClasseProcessualPJe.find((opcao) => opcao.valor === complemento.valor);
            if (opcaoClasse) {
                return opcaoClasse.texto;
            }
            opcaoClasse = this.opcoesClasseProcessualPJe.find((opcao) => opcao.texto.includes(complemento.valor));
            return opcaoClasse ? opcaoClasse.texto : '';
        } else {
            return complemento.valor;
        }
    };

    recuperarDominioComplemento(movimento, complemento) {
        let dominiosComplementos = movimento.dominiosComplementos;
        return dominiosComplementos.find(
            (dominioComplemento) => parseInt(dominioComplemento.codigo, 10) === complemento.codigoDominioComplementoCnj
        );
    }

    isComplementoPreenchido(complemento) {
        return (
            complemento && (complemento.valor !== undefined || complemento.codigoDominioComplementoCnj !== undefined)
        );
    }

    retornarDatasFeriados(feriados) {
        return feriados.map((feriado) => feriado.data);
    }

    get opcoesClasseProcessualPJe() {
        return this.getClassesProcesso(Configuracao.ORIGEM_PJE).map((classe) => ({
            texto: classe.sigla + ' - ' + classe.descricao,
            valor: classe.sigla
        }));
    }

    carregarFeriados = async () => {
        const anoAtual = new Date().getFullYear();
        const anos = [anoAtual - 1, anoAtual, anoAtual + 1];
        let calendario = await this.calendarioService.recuperarCalendario(anos);
        runInAction(() => {
            this.feriados = calendario;
            this.feriadosPublicacao = this.dataFeriadosPublicacao = this.retornarDatasFeriados(
                calendario.filter((evento) => evento.tipo === 'TODOS' || evento.tipo === 'PUBLICACAO')
            );
            this.dataFeriadosVotacao = this.retornarDatasFeriados(
                calendario.filter((evento) => evento.tipo === 'TODOS' || evento.tipo === 'VOTACAO')
            );
        });
    };

    ehFeriadoPublicacao = (data) => {
        return !!this.dataFeriadosPublicacao.includes(data.format('DD-MM-YYYY'));
    };

    ehFeriadoVotacao = (data) => {
        return !!this.dataFeriadosVotacao.includes(data.format('DD-MM-YYYY'));
    };

    ehDiaUtilPublicacao(data) {
        const diaDaSemana = data.day();
        if (diaDaSemana === 0 || diaDaSemana === 6) {
            return false;
        }
        return !this.ehFeriadoPublicacao(data);
    }

    ehDiaUtilVotacao(data) {
        const diaDaSemana = data.day();
        if (diaDaSemana === 0 || diaDaSemana === 6) {
            return false;
        }
        return !this.ehFeriadoVotacao(data);
    }

    getProximaDataUtilPublicacao(dataStr, inputPattern, outputPattern) {
        return this.proximaDataUtilPublicacao(moment(dataStr, inputPattern)).format(outputPattern);
    }

    getProximaDataUtilVotacao(dataStr, inputPattern, outputPattern) {
        return this.proximaDataUtilVotacao(moment(dataStr, inputPattern)).format(outputPattern);
    }

    proximaDataUtilPublicacao(data) {
        let proximoDiaUtil = data;
        do {
            proximoDiaUtil = proximoDiaUtil.add(1, 'days');
        } while (!this.ehDiaUtilPublicacao(proximoDiaUtil));
        return proximoDiaUtil;
    }

    proximaDataUtilVotacao(data) {
        let proximoDiaUtil = data;
        do {
            proximoDiaUtil = proximoDiaUtil.add(1, 'days');
        } while (!this.ehDiaUtilVotacao(proximoDiaUtil));
        return proximoDiaUtil;
    }
}
